import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import 'react-accessible-accordion/dist/fancy-example.css';
import { Accordion, AccordionItem, AccordionItemHeading, AccordionItemButton, AccordionItemPanel } from 'react-accessible-accordion';
import { CheckCircle as CheckIcon, Error as ErrorIcon, ArrowBack as BackIcon, ArrowForward as NextIcon, Download as DownloadIcon, Info as InfoIcon } from '@mui/icons-material';
import { Button, Stepper, Step, StepLabel, Box, Typography, Select, MenuItem, FormControl, Chip, Alert, LinearProgress, CircularProgress, Card, CardContent, Grid, Divider } from '@mui/material';

import constants from '../constants';
import { getPipelines } from '../store/Deals';
import { uploadImportFile, executeImport, clearImportData, getProperties, getTemplateCSV, validateImportData } from '../store/Objects/index.js';
import { maxHeight } from '../../node_modules/@mui/system/index';

const ImportWizard = ({ setImportWizardOpen, importWizardOpen, onComplete, objectType = 'contacts', currentPipelineId = null }) => {
  let object = constants.getObjectSchema(objectType)

  const dispatch = useDispatch();
  const pipelines = useSelector(state => state['Deals']?.['pipelines']) || [];
  const { importPreview, importStatus } = useSelector(state => state['Objects']?.[object['type']]);
  const properties = importPreview?.['properties'] || [];

  const [fileData, setFileData] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [importJob, setImportJob] = useState(null);
  const [fieldMappings, setFieldMappings] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [parsedData, setParsedData] = useState({ headers: [], rows: [], totalRows: 0 });

  const [detailedResults, setDetailedResults] = useState(null);
  const [activeObject, setActiveObject] = useState(object['type']);
  const [fieldValidationErrors, setFieldValidationErrors] = useState({});

  const [defaultStage, setDefaultStage] = useState(null);
  const [selectedPipeline, setSelectedPipeline] = useState(currentPipelineId);
  const [availablePipelines, setAvailablePipelines] = useState([]);

  const [importConfig, setImportConfig] = useState({ batchSize: 100, createOnly: false, skipFirstRow: true, duplicateHandling: 'skip', duplicateCheckFields: object?.['importConfig']?.['defaultDuplicateFields'] || [] });

  const steps = ['Select Object', 'Upload File', 'Map Fields', 'Preview & Validate', 'Import'];

  useEffect(() => {
    if (!importWizardOpen) {
      // Reset everything when wizard closes
      setActiveStep(0);
      setFileData(null);
      setImportJob(null);
      setFieldMappings({});
      setDefaultStage(null);
      setIsProcessing(false);
      setImportProgress(null);
      setDetailedResults(null);
      setSelectedPipeline(null);
      setAvailablePipelines([]);
      setValidationResults(null);
      setFieldValidationErrors({});
      dispatch(clearImportData({ objectType }));
      setParsedData({ headers: [], rows: [], totalRows: 0 });
    }
  }, [importWizardOpen]);

  useEffect(() => { dispatch(getProperties()) }, []);

  // Fetch pipelines when importing deals
  useEffect(() => {
    if (objectType === 'deals' && importWizardOpen) dispatch(getPipelines());
  }, [objectType, importWizardOpen]);

  // Update available pipelines and set default when pipelines are loaded
  useEffect(() => {
    if (pipelines.length > 0) {
      setAvailablePipelines(pipelines);
      // Set default pipeline if available and not already selected
      if (!selectedPipeline) {
        // Use currentPipelineId if provided, otherwise use default pipeline
        const targetPipelineId = currentPipelineId || (pipelines.find(p => p.isDefault) || pipelines[0])?._id;
        const targetPipeline = pipelines.find(p => p._id === targetPipelineId);
        if (targetPipeline) {
          setSelectedPipeline(targetPipeline._id);
          // Set first stage as default stage
          if (targetPipeline.stages && targetPipeline.stages.length > 0)
            setDefaultStage(targetPipeline.stages[0].id);
        }
      }
    }
  }, [pipelines, currentPipelineId]);

  // Validate field mappings when they change
  useEffect(() => {
    if (Object.keys(fieldMappings).length > 0 && parsedData.rows.length > 0)
      validateFieldMappings();
  }, [fieldMappings]);

  useEffect(() => {
    if (importPreview) {
      setImportJob({ jobId: importPreview['jobId'] });
      setParsedData({ headers: importPreview['headers'] || [], rows: importPreview['rows'] || [], totalRows: importPreview['totalRows'] || 0 });

      // Apply suggested mappings from backend
      if (importPreview['suggestedMappings']) setFieldMappings(importPreview['suggestedMappings']);

      // File processing is complete
      setIsProcessing(false);

      // Move to next step
      if (activeStep === 0) setActiveStep(1);
    }
  }, [importPreview]);

  useEffect(() => {
    if (importStatus) {
      if (importStatus['status'] === 'processing') {
        setImportProgress({ total: importStatus['progress']?.['totalRecords'] || 0, failed: importStatus['progress']?.['failedRecords'] || 0, skipped: importStatus['progress']?.['skippedRecords'] || 0, created: importStatus['progress']?.['successfulRecords'] || 0, processed: importStatus['progress']?.['processedRecords'] || 0, percentage: importStatus['progress']?.['percentComplete'] || Math.round(((importStatus['progress']?.['processedRecords'] || 0) / (importStatus['progress']?.['totalRecords'] || 1)) * 100) });
      } else if (importStatus['status'] === 'completed') {
        setImportProgress({ percentage: 100, total: importStatus['progress']?.['totalRecords'] || 0, failed: importStatus['progress']?.['failedRecords'] || 0, skipped: importStatus['progress']?.['skippedRecords'] || 0, created: importStatus['progress']?.['successfulRecords'] || 0, processed: importStatus['progress']?.['processedRecords'] || 0 });

        // Store detailed results
        if (importStatus['results'])
          setDetailedResults({
            failed: importStatus['results']['failed'] || [],
            failedTotal: importStatus['results']['failedTotal'] || 0,
            duplicates: importStatus['results']['duplicates'] || [],
            duplicatesTotal: importStatus['results']['duplicatesTotal'] || 0,
            updatedSample: importStatus['results']['updatedSample'] || [],
            updated: importStatus['results']['updated'] || 0
          });

        // Don't close wizard automatically to allow viewing results
        setIsProcessing(false);
      } else if (importStatus['status'] === 'failed') {
        setIsProcessing(false);
        toast.error('Import failed: ' + (importStatus['errorLogs']?.[0]?.['message'] || 'Unknown error'));
      }
    }
  }, [importStatus]);

  const handleFileUpload = (event) => {
    const file = event['target']['files'][0];
    if (!file) return;

    if (!file['name'].endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    // Reset parsed data when uploading a new file
    setFieldMappings({});
    setParsedData({ headers: [], rows: [], totalRows: 0 });

    setIsProcessing(true);
    setFileData({ name: file['name'], size: file['size'] });

    // Create FormData and dispatch upload action
    const formData = new FormData();
    formData.append('file', file);
    formData.append('objectType', objectType);

    dispatch(uploadImportFile({ objectType, file }));
    // Don't set isProcessing to false here - let it be controlled by the import preview response
  };

  // Reusable validation function that returns validation result
  const validatePropertyValue = (sampleValue, property, returnError = false) => {
    if (!sampleValue || sampleValue.trim() === '') {
      // Empty values are okay during mapping, but may be required fields
      if (returnError && property['required']) return { isValid: false, error: 'Required field - sample data is empty' };
      return { isValid: true };
    }

    let isValid = true;
    let error = '';

    switch (property['type']) {
      case 'email':
        isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(sampleValue);
        if (!isValid) error = `Invalid email format: "${sampleValue}"`;
        break;

      case 'phone':
        // Aggressively clean the phone number: remove all characters except digits and the plus sign
        const cleanedPhone = sampleValue.replace(/[^\d+]/g, '');
        const phoneRegex = /^[\+]?\d{7,15}$/;
        isValid = phoneRegex.test(cleanedPhone);
        if (!isValid) error = `Invalid phone format: "${sampleValue}"`;
        break;

      case 'date':
      case 'datetime':
        isValid = !isNaN(Date.parse(sampleValue));
        if (!isValid) error = `Invalid date format: "${sampleValue}"`;
        break;

      case 'url':
        try {
          // Validate URL with or without protocol, but don't modify the original value
          const testUrl = /^https?:\/\//i.test(sampleValue.trim()) ? sampleValue.trim() : 'https://' + sampleValue.trim();
          new URL(testUrl);
          isValid = true;
        } catch {
          isValid = false;
          error = `Invalid URL format: "${sampleValue}"`;
        }
        break;

      case 'number':
      case 'currency':
      case 'percentage':
        const numericValue = sampleValue.replace(/[$,%,]/g, '').trim();
        isValid = !isNaN(numericValue) && numericValue !== '';
        if (!isValid) error = `Invalid number format: "${sampleValue}"`;
        break;

      case 'select':
      case 'multiselect':
        // Special handling for deal stage field
        if (objectType === 'deals' && property['name'] === 'stage' && selectedPipeline) {
          const pipeline = availablePipelines.find(p => p['_id'] === selectedPipeline);
          if (pipeline && pipeline['stages']) {
            const validStageIds = pipeline['stages'].map(s => s['id']);
            const validStageNames = pipeline['stages'].map(s => s['name']);
            const normalizedValue = sampleValue.trim().toLowerCase().replace(/\s+/g, '-');

            isValid = validStageIds.includes(sampleValue.trim()) || validStageIds.includes(normalizedValue) || validStageNames.some(name => name.toLowerCase() === sampleValue.trim().toLowerCase());

            if (!isValid) error = `Invalid stage "${sampleValue}" - must be one of: ${validStageIds.join(', ')} (e.g., 'prospecting', 'qualification', 'closed-won')`;
          }
        } else {
          // Regular select/multiselect validation
          const options = property['fieldConfig']?.['options'] || property['options'] || [];
          if (options.length > 0) {
            const values = property['type'] === 'multiselect' ? sampleValue.split(',').map(v => v.trim()) : [sampleValue.trim()];

            const invalidValues = values.filter(value => {
              return !options.some(opt => (typeof opt === 'string' && opt === value) || (typeof opt === 'object' && (opt['value'] === value || opt['label'] === value)));
            });

            if (invalidValues.length > 0) {
              isValid = false;
              error = `Invalid option "${invalidValues.join(', ')}" - must be one of: ${options.map(o => typeof o === 'string' ? o : o['label'] || o['value']).join(', ')}`;
            }
          }
        }
        break;

      case 'boolean':
        const boolValue = sampleValue.toLowerCase().trim();
        const validBooleans = ['true', 'false', '1', '0', 'yes', 'no', 'y', 'n'];
        isValid = validBooleans.includes(boolValue);
        if (!isValid) error = `Invalid boolean value: "${sampleValue}" - use true/false, yes/no, or 1/0`;
        break;

      case 'user':
      case 'contact':
      case 'company':
      case 'reference':
        const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(sampleValue);
        const isObjectId = /^[a-f\d]{24}$/i.test(sampleValue);
        const isNumericId = /^\d+$/.test(sampleValue);

        // Only accept valid email, MongoDB ObjectId, or numeric ID - remove the length >= 3 check
        isValid = isEmail || isObjectId || isNumericId;
        if (!isValid) error = `Invalid ${property['type']} reference: "${sampleValue}" - must be a valid MongoDB ObjectId (24 hex chars), email address, or numeric ID`;
        break;

      default:
        isValid = true;
    }

    return returnError ? { isValid, error } : { isValid };
  };

  const validateFieldMappings = () => {
    const errors = {};
    const sampleRow = parsedData['rows'][0] || {};

    Object.entries(fieldMappings).forEach(([csvColumn, propertyName]) => {
      if (!propertyName) return; // Skip unmapped columns

      const property = properties.find(p => p['name'] === propertyName);
      if (!property) return;

      const sampleValue = sampleRow[csvColumn];
      const validationResult = validatePropertyValue(sampleValue, property, true);

      if (!validationResult['isValid'] && validationResult['error']) errors[csvColumn] = [validationResult['error']];
    });

    setFieldValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };


  const handleNext = async () => {

    if (activeStep === 1 && !fileData) {
      toast.error('Please upload a CSV file');
      return;
    }

    if (activeStep === 2) {
      // Check if required fields are mapped based on objectType
      const mappedFields = Object.values(fieldMappings);
      let requiredFields = properties.filter(p => p['required']).map(p => p['name']);

      // For deals, if defaultStage is selected, remove stage from required fields
      if (objectType === 'deals' && defaultStage && !mappedFields.includes('stage')) {
        requiredFields = requiredFields.filter(field => field !== 'stage');
      }

      // For contacts, special validation: at least one of firstName, lastName, or email must be mapped
      if (objectType === 'contacts') {
        // Remove firstName and lastName from required fields since they're now optional
        requiredFields = requiredFields.filter(field => field !== 'firstName' && field !== 'lastName');

        // Check if at least one of firstName, lastName, or email is mapped
        const hasNameOrEmail = mappedFields.includes('firstName') ||
          mappedFields.includes('lastName') ||
          mappedFields.includes('email');

        if (!hasNameOrEmail) {
          toast.error('At least one of First Name, Last Name, or Email must be mapped for contacts');
          return;
        }
      }

      const missingRequired = requiredFields.filter(field => !mappedFields['includes'](field));

      if (missingRequired['length'] > 0) {
        const missingLabels = missingRequired.map(field => {
          const prop = properties.find(p => p['name'] === field);
          return prop ? prop['label'] : field;
        });
        toast.error(`Please map required fields: ${missingLabels['join'](', ')}`);
        return;
      }

      // For deals, check if stage is mapped or default stage is selected
      if (objectType === 'deals') {
        const stageMapped = mappedFields.includes('stage');
        if (!stageMapped && !defaultStage) {
          toast.error('Please either map a stage column or select a default stage for all deals');
          return;
        }
      }

      // Validate field mappings for correct data types
      const isValid = validateFieldMappings();
      if (!isValid) {
        const errorCount = Object.keys(fieldValidationErrors).length;
        const firstError = Object.entries(fieldValidationErrors)[0];
        toast.error(`Fix ${errorCount} validation error${errorCount > 1 ? 's' : ''}: ${firstError[1][0]}`);
        return;
      }
    }

    // Validate before moving to preview step
    if (activeStep === 2) {
      await onValidateImportData();
      // Don't proceed automatically - let validation callback handle it
      return;
    }

    // Start import
    if (activeStep === 3) {
      await startImportProcess();
      setActiveStep(prev => prev + 1);
      return;
    }

    if (activeStep === 4) {
      // Only close if import is complete and user clicks Finish
      if (importProgress?.percentage === 100) {
        setImportWizardOpen(false);
        onComplete();
      }
      return;
    }

    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const downloadFailedRecords = () => {
    if (!detailedResults?.failed || detailedResults.failed.length === 0) return;

    // Prepare CSV content
    const csvRows = [];

    // Get all unique field names from failed records
    const allFields = new Set(['Row Number', 'Error']);
    detailedResults.failed.forEach(record => {
      Object.keys(record.data || {}).forEach(key => {
        if (!['customFields', 'source', 'sourceDetails', 'workspaceId', 'createdBy', 'importJobId', 'pipelineId'].includes(key)) {
          allFields.add(key);
        }
      });
    });

    // Add headers
    csvRows.push(Array.from(allFields).join(','));

    // Add data rows
    detailedResults.failed.forEach(record => {
      const row = [];
      row.push(record.row); // Row number
      row.push(`"${(record.errorMessages || []).join('; ').replace(/"/g, '""')}"`); // Error messages

      // Add field values
      Array.from(allFields).slice(2).forEach(field => {
        const value = record.data?.[field] || '';
        // Escape quotes and wrap in quotes if contains comma
        const escapedValue = String(value).replace(/"/g, '""');
        row.push(escapedValue.includes(',') ? `"${escapedValue}"` : escapedValue);
      });

      csvRows.push(row.join(','));
    });

    // Create and download file
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `failed_${objectType}_import_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const onValidateImportData = async () => {
    setIsProcessing(true);

    try {
      // Call backend validation through saga
      // Prepare config with deals-specific settings
      let validationConfig = { ...importConfig };
      if (objectType === 'deals') {
        if (selectedPipeline) validationConfig['pipelineId'] = selectedPipeline;

        // Include defaultStage if no stage field is mapped
        const stageMapped = Object.values(fieldMappings).includes('stage');
        if (!stageMapped && defaultStage) validationConfig['defaultStage'] = defaultStage;
      }

      dispatch(validateImportData({
        objectType,
        jobId: importJob?.['jobId'],
        fieldMapping: fieldMappings,
        config: validationConfig,
        successCallback: (validationResult) => {
          // Update validation results for display
          setValidationResults({
            totalRecords: validationResult['totalRows'],
            duplicateHandling: importConfig['duplicateHandling'],
            mappedFields: validationResult['summary']['mappedFields'],
            requiredFieldsMapped: validationResult['summary']['missingRequired'].length === 0,
            preview: parsedData['rows']['slice'](0, 5)['map'](row => {
              const mapped = {};
              Object.entries(fieldMappings)['forEach'](([source, target]) => { mapped[target] = row[source]; });
              return mapped;
            }),
            // Add validation results from backend
            isValid: validationResult['valid'],
            warnings: validationResult['warnings'],
            validationErrors: validationResult['fieldErrors'],
          });

          // Update field validation errors for UI display
          if (validationResult['fieldErrors']) {
            const uiFieldErrors = {};
            Object.entries(validationResult['fieldErrors']).forEach(([column, errorData]) => {
              // Get the first few errors to display
              const errorMessages = errorData.errors.slice(0, 3).map(e => e.error);
              if (errorData.totalErrors > 3) {
                errorMessages.push(`...and ${errorData.totalErrors - 3} more errors`);
              }
              uiFieldErrors[column] = errorMessages;
            });
            setFieldValidationErrors(uiFieldErrors);
          }

          // Show warnings if any
          if (validationResult['warnings'] && validationResult['warnings'].length > 0)
            validationResult['warnings'].forEach(warning => toast.warning(warning));

          // Show errors if validation failed
          if (!validationResult['valid']) {
            if (validationResult['errors'] && validationResult['errors'].length > 0)
              toast.error(validationResult['errors'][0]);
            else if (validationResult['summary']['dataTypeErrors'] > 0)
              toast.error(`Found ${validationResult['summary']['dataTypeErrors']} data validation errors. Please review and fix the mappings.`);
          } else {
            // Only proceed to next step if validation is successful
            setActiveStep(prev => prev + 1);
          }

          setIsProcessing(false);
        },
        failCallback: (error) => {
          toast.error('Validation failed: ' + (error?.['response']?.['data']?.['message'] || 'Unknown error'));
          setIsProcessing(false);
        }
      }));
    } catch (error) {
      toast.error('Validation failed');
      setIsProcessing(false);
    }
  }

  const startImportProcess = async () => {
    setIsProcessing(true);

    if (!importJob?.['jobId']) {
      toast.error('No import job found');
      setIsProcessing(false);
      return;
    }

    // Prepare import config with deals-specific settings
    let finalImportConfig = { ...importConfig };
    if (objectType === 'deals') {
      if (selectedPipeline) finalImportConfig['pipelineId'] = selectedPipeline;

      // Include defaultStage if no stage field is mapped
      const stageMapped = Object.values(fieldMappings).includes('stage');
      if (!stageMapped && defaultStage) finalImportConfig['defaultStage'] = defaultStage;
    }

    // Dispatch execute import action with field mappings and config
    dispatch(executeImport({ objectType, config: { jobId: importJob['jobId'], fieldMapping: fieldMappings, ...finalImportConfig, objectType } }));
  }

  const columns = [
    { name: 'CSV Column', selector: row => row['header'], sortable: false, width: '200px' },
    { name: 'Sample Data', cell: row => <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>{row['sample']}</Typography>, sortable: false, grow: 1 },
    {
      name: 'Map to Field',
      width: '250px',
      cell: row => (
        <FormControl fullWidth size="small" error={!!fieldValidationErrors[row['header']]}>
          <Select
            value={fieldMappings[row['header']] || ''}
            onChange={(e) => {
              setFieldMappings((prev) => ({ ...prev, [row['header']]: e['target']['value'] }));
              // Clear validation error for this field when mapping changes
              setFieldValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[row['header']];
                return newErrors;
              });
              // Re-validate will happen automatically in useEffect
            }}
          >
            <MenuItem value=""> <em>Skip this column</em> </MenuItem>
            <Divider />
            <MenuItem disabled> <Typography variant="caption">System Fields</Typography> </MenuItem>
            {properties.filter((prop) => prop['isSystem']).map((prop) => <MenuItem key={prop['name']} value={prop['name']}> {prop['label']} {prop['required'] && '*'} </MenuItem>)}
            <Divider />
            <MenuItem disabled> <Typography variant="caption">Custom Fields</Typography> </MenuItem>
            {properties.filter((prop) => !prop['isSystem']).map((prop) => <MenuItem key={prop['name']} value={prop['name']}> {prop['label']} {prop['required'] && '*'} </MenuItem>)}
          </Select>
        </FormControl>
      ),
      grow: 2,
    },
    {
      width: '300px',
      name: 'Validation',
      cell: row => {
        const isMapped = !!fieldMappings[row['header']];
        const errors = fieldValidationErrors[row['header']];

        if (!isMapped) return <Typography variant="caption" color="text.secondary">Not mapped</Typography>;

        if (errors && errors.length > 0)
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon color="error" fontSize="small" />
              <Typography variant="caption" color="error" sx={{ flex: 1 }}>
                {errors[0]}
              </Typography>
            </Box>
          )

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckIcon color="success" fontSize="small" />
            <Typography variant="caption" color="success.main">Mapped</Typography>
          </Box>
        );
      },
      grow: 2,
    },
  ]

  // Prepare data
  const dataPreview = validationResults?.['preview'] || [];
  const mappingData = parsedData['headers']['map']((header) => ({ header, sample: parsedData['rows'][0]?.[header] || '' }));
  const columnsDataPreview = Object.values(fieldMappings)['filter'](Boolean)['map']((field, index) => ({ name: field, selector: (row) => row[field], sortable: false, wrap: true, width: (index === 0 || index === 5) ? '250px' : '120px' }));

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Select Object 
        return (
          <div className='object-selection'>
            <div className='title-import-modal'>
              <h4>What kind of data is in your file?</h4>
              <p>Select one or more of the objects you want to create and associate.</p>
            </div>
            <div className='object-list-area'>
              {Object.values(constants['objectsSchema'])['map']((box, index) => (
                <div
                  key={index}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setActiveObject(box['type'])}
                  className={`object-list-item ${activeObject === box['type'] ? 'active' : ''}`}
                >
                  <span className={`custom-checkbox ${activeObject === box['type'] ? 'checked' : ''}`} />
                  <div className='img-box'> <img alt={object['types']} src={require(`../static/img/${box['image']}`)} /></div>
                  <div className='text-box'>
                    <h5>{box['plural']}</h5>
                    <p>{box['description']}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* <div className='past-table'>
              <h4>Past imports</h4>
              <DataTable
                columns={columnsPast}
                data={dataPast}
                dense
                fixedHeader
                 style={{ maxHeight: '100%' }}
                className='import-modal-table'
              />
            </div> */}
          </div>
        )

      case 1: // Upload File
        return (
          <div className='object-selection'>
            <div className='title-import-modal'>
              <h4>Upload your files</h4>
              <p>Before you upload your files below, make sure your file is <button className='import-btn-text'>  ready to be imported</button></p>
            </div>
            <div className='import-csv'>
              <label htmlFor="csv-upload">
                <svg width="51" height="51" viewBox="0 0 51 51" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g opacity="0.6">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M31.3181 5.86871H17.1805C12.7605 5.85171 9.13734 9.37496 9.03322 13.7928V36.558C8.93547 41.0481 12.4948 44.769 16.985 44.8688C17.0508 44.8688 17.1146 44.871 17.1805 44.8688H34.1571C38.6068 44.6882 42.1131 41.012 42.0812 36.558V17.0802L31.3181 5.86871Z" stroke="#9CA3AF" stroke-width="3.1875" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M30.7607 5.84375V12.0254C30.7607 15.0429 33.2002 17.4888 36.2177 17.4973H42.0721" stroke="#9CA3AF" stroke-width="3.1875" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M30.3637 32.6367H18.8887" stroke="#9CA3AF" stroke-width="3.1875" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M26.0182 24.6626H18.8867" stroke="#9CA3AF" stroke-width="3.1875" stroke-linecap="round" stroke-linejoin="round" />
                  </g>
                </svg>
                <p>Drag and drop your CSV file here, or</p>
                <input type="file" accept=".csv" onChange={handleFileUpload} id="csv-upload" style={{ cursor: 'pointer' }} />
                <button className='btn-style-new' onChange={handleFileUpload} component="span" disabled={isProcessing}>
                  Choose File
                </button>
              </label>
              <p>Upload a CSV file containing your {objectType} data. The first row should contain column headers.</p>

              {fileData && (
                <Card sx={{ mt: 3 }}>
                  <CardContent>
                    <Typography variant="h6">{fileData['name']}</Typography>
                    <Typography variant="body2" color="text.secondary"> Size: {(fileData['size'] / 1024)['toFixed'](2)} KB</Typography>
                    {parsedData['totalRows'] > 0 && (
                      <>
                        <Typography variant="body2" color="text.secondary"> Total Rows: {parsedData['totalRows']}</Typography>
                        <Typography variant="body2" color="text.secondary"> Columns: {parsedData['headers']['join'](', ')}</Typography>
                      </>
                    )}
                    {isProcessing && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">Processing file...</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
              <div className='template-download'>
                <div className='template-download-text'>
                  <div className='left-text'>
                    <i className='icon'><InfoIcon /></i>
                    <p>Need a template?</p>
                  </div>
                  <div className='right-text'>
                    {/* <button className='btn-style-border' onClick={() => window.open('https://sellagent-agent-files.s3.us-east-2.amazonaws.com/sample_contacts_crm.csv', '_blank')}> */}
                    <button className='btn-style-border' onClick={() => dispatch(getTemplateCSV(object['type']))}>
                      Download CSV Template
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case 2: // Map Fields
        return (
          <div className='object-selection'>
            <div className='title-import-modal'>
              <h4>Map columns in your file to {object['name']} properties</h4>
              <p>Map your CSV columns to {object['name']} fields</p>
            </div>
            <div className='mapping-instructions'>
              <h5>Mapping Guide</h5>
              <div className='accordioan-box'>
                <Accordion allowZeroExpanded>
                  <AccordionItem>
                    <AccordionItemHeading>
                      <AccordionItemButton>
                        <i>
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 6C7.38071 6 8.5 4.88071 8.5 3.5C8.5 2.11929 7.38071 1 6 1C4.61929 1 3.5 2.11929 3.5 3.5C3.5 4.88071 4.61929 6 6 6Z" fill="#4B5563" />
                            <path d="M6.00008 7.25C3.49508 7.25 1.45508 8.93 1.45508 11C1.45508 11.14 1.56508 11.25 1.70508 11.25H10.2951C10.4351 11.25 10.5451 11.14 10.5451 11C10.5451 8.93 8.50508 7.25 6.00008 7.25Z" fill="#4B5563" />
                          </svg>
                        </i>
                        {object['plural']} Configuration
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel>
                      <div className='accordioan-box-content'>

                        <div className='accordioan-box-content-text'>
                          <h5>
                            <i className='icon'>
                              <svg width="9" height="8" viewBox="0 0 9 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.56407 0.990345L3.91769 2.34396C4.14257 2.56884 4.51017 2.56884 4.73505 2.34396L6.08867 0.990345C6.45194 0.627074 6.19678 0 5.67782 0H2.97059C2.45596 0 2.19648 0.627074 2.56407 0.990345Z" fill="#4B5563" />
                                <path d="M2.56407 6.6297L3.91769 5.27608C4.14257 5.0512 4.51017 5.0512 4.73505 5.27608L6.08867 6.6297C6.45194 6.99297 6.19678 7.62004 5.67782 7.62004H2.97059C2.45596 7.62004 2.19648 6.99297 2.56407 6.6297Z" fill="#4B5563" />
                              </svg>
                            </i>
                            Duplicate Handling
                          </h5>
                          <div className='accordioan-box-content-text-desc'>
                            <FormControl fullWidth margin="normal">
                              <Select
                                value={importConfig['duplicateHandling']}
                                onChange={(e) =>
                                  setImportConfig({ ...importConfig, duplicateHandling: e['target']['value'] })
                                }
                                displayEmpty
                                size="small"
                                renderValue={(selected) => {
                                  if (!selected) {
                                    return <em>Select an option</em>;
                                  }
                                  switch (selected) {
                                    case 'skip':
                                      return 'Skip duplicates';
                                    case 'update':
                                      return 'Update existing contacts';
                                    case 'create_new':
                                      return 'Create new contacts anyway';
                                    default:
                                      return selected;
                                  }
                                }}
                              >
                                <MenuItem value="skip">Skip duplicates</MenuItem>
                                <MenuItem value="update">Update existing contacts</MenuItem>
                                <MenuItem value="create_new">Create new contacts anyway</MenuItem>
                              </Select>
                            </FormControl>
                            {/* <FormControl component="fieldset">
                              <RadioGroup value={importConfig['duplicateHandling']} onChange={(e) => setImportConfig({ ...importConfig, duplicateHandling: e['target']['value'] })}>
                                <FormControlLabel value="skip" control={<Radio />} label="Skip duplicates" />
                                <FormControlLabel value="update" control={<Radio />} label="Update existing contacts" />
                                <FormControlLabel value="create_new" control={<Radio />} label="Create new contacts anyway" />
                              </RadioGroup>
                            </FormControl> */}
                          </div>
                        </div>

                        <div className='accordioan-box-content-text'>
                          <h5>
                            <i className='icon'>
                              <svg width="9" height="8" viewBox="0 0 9 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.56407 0.990345L3.91769 2.34396C4.14257 2.56884 4.51017 2.56884 4.73505 2.34396L6.08867 0.990345C6.45194 0.627074 6.19678 0 5.67782 0H2.97059C2.45596 0 2.19648 0.627074 2.56407 0.990345Z" fill="#4B5563" />
                                <path d="M2.56407 6.6297L3.91769 5.27608C4.14257 5.0512 4.51017 5.0512 4.73505 5.27608L6.08867 6.6297C6.45194 6.99297 6.19678 7.62004 5.67782 7.62004H2.97059C2.45596 7.62004 2.19648 6.99297 2.56407 6.6297Z" fill="#4B5563" />
                                <path d="M0.0681132 3.56381L1.05848 2.00693C1.21416 1.76043 1.56014 1.7215 1.76772 1.92909L3.32459 3.48596C3.5019 3.66327 3.5019 3.95302 3.32459 4.13033L1.76772 5.68721C1.56014 5.89479 1.21849 5.85587 1.05848 5.60936L0.0681132 4.05249C-0.0227044 3.90545 -0.0227044 3.71517 0.0681132 3.56381Z" fill="#4B5563" />
                                <path d="M8.57891 3.56381L7.58855 2.00693C7.43286 1.76043 7.08688 1.7215 6.8793 1.92909L5.32244 3.48596C5.14513 3.66327 5.14513 3.95302 5.32244 4.13033L6.8793 5.68721C7.08688 5.89479 7.42854 5.85587 7.58855 5.60936L8.57891 4.05249C8.66973 3.90545 8.66973 3.71517 8.57891 3.56381Z" fill="#4B5563" />
                              </svg>
                            </i>
                            Check for duplicates using:
                          </h5>
                          <div className='filtes-box'>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {(object?.['importConfig']?.['duplicateCheckOptions'] || [])['map'](field => (
                                <Chip
                                  key={field}
                                  label={field}
                                  onClick={() => {
                                    const fields = importConfig['duplicateCheckFields'];
                                    if (fields['includes'](field)) setImportConfig({ ...importConfig, duplicateCheckFields: fields.filter(f => f !== field) });
                                    else setImportConfig({ ...importConfig, duplicateCheckFields: [...fields, field] });
                                  }}
                                  color={importConfig['duplicateCheckFields']['includes'](field) ? 'primary' : 'default'}
                                  variant={importConfig['duplicateCheckFields']['includes'](field) ? 'filled' : 'outlined'}
                                />
                              ))}
                            </Box>
                          </div>
                        </div>

                        {objectType === 'deals' && (
                          <div className='accordioan-box-content-text' style={{ width: '100%' }}>
                            <h5>
                              <i className='icon'>
                                <svg width="9" height="8" viewBox="0 0 9 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M2.56407 0.990345L3.91769 2.34396C4.14257 2.56884 4.51017 2.56884 4.73505 2.34396L6.08867 0.990345C6.45194 0.627074 6.19678 0 5.67782 0H2.97059C2.45596 0 2.19648 0.627074 2.56407 0.990345Z" fill="#4B5563" />
                                  <path d="M2.56407 6.6297L3.91769 5.27608C4.14257 5.0512 4.51017 5.0512 4.73505 5.27608L6.08867 6.6297C6.45194 6.99297 6.19678 7.62004 5.67782 7.62004H2.97059C2.45596 7.62004 2.19648 6.99297 2.56407 6.6297Z" fill="#4B5563" />
                                  <path d="M0.0681132 3.56381L1.05848 2.00693C1.21416 1.76043 1.56014 1.7215 1.76772 1.92909L3.32459 3.48596C3.5019 3.66327 3.5019 3.95302 3.32459 4.13033L1.76772 5.68721C1.56014 5.89479 1.21849 5.85587 1.05848 5.60936L0.0681132 4.05249C-0.0227044 3.90545 -0.0227044 3.71517 0.0681132 3.56381Z" fill="#4B5563" />
                                  <path d="M8.57891 3.56381L7.58855 2.00693C7.43286 1.76043 7.08688 1.7215 6.8793 1.92909L5.32244 3.48596C5.14513 3.66327 5.14513 3.95302 5.32244 4.13033L6.8793 5.68721C7.08688 5.89479 7.42854 5.85587 7.58855 5.60936L8.57891 4.05249C8.66973 3.90545 8.66973 3.71517 8.57891 3.56381Z" fill="#4B5563" />
                                </svg>
                              </i>
                              Pipeline Selection
                            </h5>
                            <div className='accordioan-box-content-text-desc'>
                              <FormControl fullWidth margin="normal">
                                <Select
                                  size="small"
                                  displayEmpty
                                  value={selectedPipeline || ''}
                                  onChange={(e) => setSelectedPipeline(e.target.value)}
                                  renderValue={(selected) => {
                                    if (!selected) return <em>Select a pipeline</em>;
                                    const pipeline = availablePipelines.find(p => p._id === selected);
                                    return pipeline ? `${pipeline.name}${pipeline.isDefault ? ' (Default)' : ''}` : selected;
                                  }}
                                >
                                  {availablePipelines.map((pipeline) => (
                                    <MenuItem key={pipeline._id} value={pipeline._id}>
                                      {pipeline.name} {pipeline.isDefault && '(Default)'}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              {selectedPipeline && availablePipelines.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Available stages for mapping:
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                    {availablePipelines.find(p => p._id === selectedPipeline)?.stages?.map(stage => (
                                      <Chip
                                        key={stage.id}
                                        label={`${stage.name} (${stage.id})`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.75rem' }}
                                      />
                                    ))}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Use the stage ID values (e.g., 'prospecting', 'qualification') in your CSV file
                                  </Typography>
                                </Box>
                              )}

                              {/* Default Stage Selector */}
                              {selectedPipeline && availablePipelines.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                  <h5>
                                    <i className='icon'>
                                      <svg width="9" height="8" viewBox="0 0 9 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M2.56407 0.990345L3.91769 2.34396C4.14257 2.56884 4.51017 2.56884 4.73505 2.34396L6.08867 0.990345C6.45194 0.627074 6.19678 0 5.67782 0H2.97059C2.45596 0 2.19648 0.627074 2.56407 0.990345Z" fill="#4B5563" />
                                        <path d="M2.56407 6.6297L3.91769 5.27608C4.14257 5.0512 4.51017 5.0512 4.73505 5.27608L6.08867 6.6297C6.45194 6.99297 6.19678 7.62004 5.67782 7.62004H2.97059C2.45596 7.62004 2.19648 6.99297 2.56407 6.6297Z" fill="#4B5563" />
                                        <path d="M0.0681132 3.56381L1.05848 2.00693C1.21416 1.76043 1.56014 1.7215 1.76772 1.92909L3.32459 3.48596C3.5019 3.66327 3.5019 3.95302 3.32459 4.13033L1.76772 5.68721C1.56014 5.89479 1.21849 5.85587 1.05848 5.60936L0.0681132 4.05249C-0.0227044 3.90545 -0.0227044 3.71517 0.0681132 3.56381Z" fill="#4B5563" />
                                        <path d="M8.57891 3.56381L7.58855 2.00693C7.43286 1.76043 7.08688 1.7215 6.8793 1.92909L5.32244 3.48596C5.14513 3.66327 5.14513 3.95302 5.32244 4.13033L6.8793 5.68721C7.08688 5.89479 7.42854 5.85587 7.58855 5.60936L8.57891 4.05249C8.66973 3.90545 8.66973 3.71517 8.57891 3.56381Z" fill="#4B5563" />
                                      </svg>
                                    </i>
                                    Default Stage (if not mapped from CSV)
                                  </h5>
                                  <FormControl fullWidth size="small">
                                    <Select
                                      value={defaultStage || ''}
                                      onChange={(e) => setDefaultStage(e.target.value)}
                                      displayEmpty
                                      renderValue={(selected) => {
                                        if (!selected) return <em>Select default stage</em>;
                                        const stage = availablePipelines.find(p => p._id === selectedPipeline)?.stages?.find(s => s.id === selected);
                                        return stage ? stage.name : selected;
                                      }}
                                    >
                                      {availablePipelines.find(p => p._id === selectedPipeline)?.stages?.map(stage => (
                                        <MenuItem key={stage.id} value={stage.id}>
                                          {stage.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  {!fieldMappings[Object.keys(fieldMappings).find(key => fieldMappings[key] === 'stage')] && (
                                    <Alert severity="info" className='info-alert-wizard' sx={{ mt: 2 }}>
                                      <Typography variant="caption">
                                        No stage column detected in your CSV. All deals will be imported to the "{availablePipelines.find(p => p._id === selectedPipeline)?.stages?.find(s => s.id === defaultStage)?.name}" stage.
                                      </Typography>
                                    </Alert>
                                  )}
                                </Box>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionItemPanel>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
            {Object.keys(fieldValidationErrors).length > 0 && (
              <div className='error-box-maping'>
                <i>
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.5996 9.95L9.59956 2.75C9.06206 1.78125 8.31831 1.25 7.49956 1.25C6.68081 1.25 5.93706 1.78125 5.39956 2.75L1.39956 9.95C0.893309 10.8687 0.837059 11.75 1.24331 12.4438C1.64956 13.1375 2.44956 13.5187 3.49956 13.5187H11.4996C12.5496 13.5187 13.3496 13.1375 13.7558 12.4438C14.1621 11.75 14.1058 10.8625 13.5996 9.95Z" fill="#EF4D56" />
                    <path d="M7.5 9.21875C7.24375 9.21875 7.03125 9.00625 7.03125 8.75V5.625C7.03125 5.36875 7.24375 5.15625 7.5 5.15625C7.75625 5.15625 7.96875 5.36875 7.96875 5.625V8.75C7.96875 9.00625 7.75625 9.21875 7.5 9.21875Z" fill="white" />
                    <path d="M7.5 11.2501C7.4625 11.2501 7.41875 11.2438 7.375 11.2376C7.3375 11.2313 7.3 11.2188 7.2625 11.2001C7.225 11.1876 7.1875 11.1688 7.15 11.1438C7.11875 11.1188 7.0875 11.0938 7.05625 11.0688C6.94375 10.9501 6.875 10.7876 6.875 10.6251C6.875 10.4626 6.94375 10.3001 7.05625 10.1813C7.0875 10.1563 7.11875 10.1313 7.15 10.1063C7.1875 10.0813 7.225 10.0626 7.2625 10.0501C7.3 10.0313 7.3375 10.0188 7.375 10.0126C7.45625 9.99385 7.54375 9.99385 7.61875 10.0126C7.6625 10.0188 7.7 10.0313 7.7375 10.0501C7.775 10.0626 7.8125 10.0813 7.85 10.1063C7.88125 10.1313 7.9125 10.1563 7.94375 10.1813C8.05625 10.3001 8.125 10.4626 8.125 10.6251C8.125 10.7876 8.05625 10.9501 7.94375 11.0688C7.9125 11.0938 7.88125 11.1188 7.85 11.1438C7.8125 11.1688 7.775 11.1876 7.7375 11.2001C7.7 11.2188 7.6625 11.2313 7.61875 11.2376C7.58125 11.2438 7.5375 11.2501 7.5 11.2501Z" fill="white" />
                  </svg>
                </i>
                {Object.keys(fieldValidationErrors).length} validation error{Object.keys(fieldValidationErrors).length > 1 ? 's' : ''} found. Please fix the mappings before proceeding.
              </div>
            )}
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <h4>All mapped statuses</h4>
              </Box>

              <DataTable
                dense
                fixedHeader
                columns={columns}
                data={mappingData}
                className='import-modal-table'
                style={{ maxHeight: '100%' }}
              />
            </Box>
          </div>
        )

      case 3: // Preview & Validate
        return (
          <>
            <div className='object-selection style-preview'>
              <div className='title-import-modal'>
                <h4>Preview & Validate</h4>
                <p>Review your data before importing</p>
              </div>
              <Box>
                {validationResults && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom> Total Records </Typography>
                          <Typography variant="h6"> {validationResults['totalRecords']} </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom> Mapped Fields </Typography>
                          <Typography variant="h6"> {validationResults['mappedFields']} </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom> Duplicate Handling </Typography>
                          <Typography variant="h6"> {importConfig['duplicateHandling']['replace']('_', ' ')} </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom>
                            Status
                          </Typography>
                          {validationResults['requiredFieldsMapped'] ?
                            <Chip icon={<CheckIcon />} label="Ready" color="success" />
                            : <Chip icon={<ErrorIcon />} label="Missing Fields" color="error" />}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
                <div className='title-import-modal style-table'>
                  <h6>Data Preview (First 5 records after mapping)</h6>
                </div>
                <DataTable
                  columns={columnsDataPreview}
                  data={dataPreview}
                  dense
                  fixedHeader
                  style={{ maxHeight: '100%' }}
                  className='import-modal-table style-progress'
                />
              </Box>
            </div>
          </>

        );

      case 4: // Import Progress
        return (
          <>
            <div className='object-selection'>
              <div className='title-import-modal'>
                <h4>Importing {object['type']}</h4>
                <p>Map your CSV columns to {object['type']} fields</p>
              </div>
              <div className='import-progress'>
                <div className='file-name-box'>
                  <h5>File name</h5>
                  <div className='file-name'> {fileData['name']} </div>
                </div>
                <Box>
                  {importProgress && (
                    <Box sx={{ mt: 3 }}>
                      {importProgress['percentage'] === 100
                        ? <div className='progress-bar-box'>
                          <h5>Importing your {object['type']} completed</h5>
                          <p>Import your {object['type']}</p>
                          <Alert severity="success" sx={{ mt: 3 }}>
                            {object['plural']} imported successfully!
                          </Alert>
                        </div>
                        : <div className='progress-bar-box'>
                          <i className='icon'>
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect width="48" height="48" rx="8" fill="#EFF6FF" />
                              <path d="M17.541 16.5248C19.6478 14.3206 23.0089 13.4561 25.9072 14.3451C28.5619 15.1732 30.4371 17.3526 31.1191 20.3969C33.445 20.9205 35.2961 22.6743 36.0391 25.1097C36.8428 27.7524 36.112 30.4684 34.127 32.1976C32.8605 33.3544 31.2047 34.0003 29.4756 34.0004H28.4404L25.1006 33.9808C25.1012 31.8158 25.1011 28.6428 25.1025 26.5062C25.1018 26.4043 25.1146 26.3022 25.126 26.1303C25.2441 26.2399 25.3138 26.2999 25.3789 26.3656C25.8446 26.837 26.3081 27.3131 26.7773 27.7816C26.9893 27.9936 27.2447 28.1041 27.5488 28.0219C28.0504 27.8863 28.2589 27.3188 27.9658 26.8773C27.8836 26.7533 27.7745 26.645 27.6689 26.5394C26.8261 25.6937 25.9831 24.8477 25.1367 24.0062C24.4889 23.362 24.1901 23.3635 23.543 24.0092C22.6916 24.8591 21.8425 25.7113 20.9961 26.5648C20.8913 26.6704 20.7896 26.7861 20.7109 26.9115C20.488 27.2672 20.5368 27.6071 20.833 27.8637C21.1158 28.1081 21.5158 28.1319 21.8008 27.8627C22.3268 27.365 22.825 26.837 23.334 26.3217C23.3814 26.2742 23.4231 26.2208 23.5449 26.1762C23.5584 26.2867 23.583 26.3974 23.583 26.5072C23.5851 28.6411 23.5833 31.799 23.584 33.9721L19.916 33.9515L16.835 34.0004C13.4982 33.769 12 31.199 12 28.9096C12 26.8637 13.1933 24.5985 15.7871 23.9652C15.0687 21.1278 15.6779 18.4611 17.541 16.5248Z" fill="#2563EB" />
                            </svg>
                          </i>
                          <h5>Importing {object['type']}...</h5>
                          <p>Please wait while we process your {object['type']}</p>
                          <LinearProgress variant="determinate" value={importProgress['percentage']} sx={{ height: 10, borderRadius: 5 }} />
                          <div className='title-import-modal'>
                            <p>{importProgress['processed']} / {importProgress['total']} ({importProgress['percentage']}%)</p>
                          </div>
                        </div>
                      }
                      <div className='import-progress-details'>
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                          <Grid item xs={4}>
                            <Card>
                              <CardContent>
                                <Typography color="success.main" variant="h6">
                                  {importProgress['created']}
                                </Typography>
                                <Typography variant="body2">Created</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={4}>
                            <Card>
                              <CardContent>
                                <Typography color="error.main" variant="h6">
                                  {importProgress['failed']}
                                </Typography>
                                <Typography variant="body2">Failed</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={4}>
                            <Card>
                              <CardContent>
                                <Typography color="warning.main" variant="h6">
                                  {importProgress['skipped']}
                                </Typography>
                                <Typography variant="body2">Skipped</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </div>

                      {/* Detailed Results Section */}
                      {detailedResults && importProgress?.percentage === 100 && (
                        <Box sx={{ mt: 4 }}>
                          {detailedResults['updated'] > 0 || detailedResults['failedTotal'] > 0 || detailedResults['duplicatesTotal'] > 0 &&
                            <Typography variant="h6" sx={{ mb: 2 }}>Import Details</Typography>
                          }
                          <Accordion allowZeroExpanded>
                            {/* Failed Records */}
                            {detailedResults['failedTotal'] > 0 && (
                              <AccordionItem>
                                <AccordionItemHeading>
                                  <AccordionItemButton>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <ErrorIcon color="error" />
                                      <Typography>Failed Records ({detailedResults.failedTotal})</Typography>
                                    </Box>
                                  </AccordionItemButton>
                                </AccordionItemHeading>
                                <AccordionItemPanel>
                                  <button className='btn-style-border mb-3'
                                    onClick={downloadFailedRecords}>
                                    <DownloadIcon />
                                    Download Failed Records
                                  </button>
                                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                    <DataTable
                                      columns={[
                                        { name: 'Row #', selector: row => row.row, sortable: true, width: '90px' },
                                        { name: 'Error', selector: row => row.errorMessages?.join(', '), wrap: true },
                                        {
                                          name: 'Data',
                                          cell: row => (
                                            <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                                              {Object.entries(row.data || {})
                                                .filter(([key]) => !['customFields', 'source', 'sourceDetails', 'workspaceId', 'createdBy', 'importJobId', 'pipelineId'].includes(key))
                                                .map(([key, value]) => `${key}: ${value}`)
                                                .join(', ')}
                                            </Typography>
                                          ),
                                          wrap: true
                                        }
                                      ]}
                                      data={detailedResults.failed}
                                      dense
                                      fixedHeader
                                      style={{ maxHeight: '100%' }}
                                      className='import-modal-table'
                                    />
                                    {detailedResults.failedTotal > 20 && (
                                      <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                        Showing first 20 of {detailedResults.failedTotal} failed records
                                      </Typography>
                                    )}
                                  </Box>
                                </AccordionItemPanel>
                              </AccordionItem>
                            )}

                            {/* Duplicate Records */}
                            {detailedResults['duplicatesTotal'] > 0 && (
                              <AccordionItem>
                                <AccordionItemHeading>
                                  <AccordionItemButton>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <InfoIcon color="warning" />
                                      <Typography>Duplicate Records ({detailedResults.duplicatesTotal})</Typography>
                                    </Box>
                                  </AccordionItemButton>
                                </AccordionItemHeading>
                                <AccordionItemPanel>
                                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                                    <DataTable
                                      columns={[
                                        { name: 'Row #', selector: row => row.row, sortable: true, width: '80px' },
                                        { name: 'Existing Record ID', selector: row => row.existingRecordId, width: '200px' },
                                        {
                                          name: 'Duplicate Data',
                                          cell: row => (
                                            <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                                              {Object.entries(row.data || {})
                                                .filter(([key]) => !['customFields', 'source', 'sourceDetails', 'workspaceId', 'createdBy', 'importJobId', 'pipelineId'].includes(key))
                                                .map(([key, value]) => `${key}: ${value}`)
                                                .join(', ')}
                                            </Typography>
                                          ),
                                          wrap: true
                                        }
                                      ]}
                                      data={detailedResults.duplicates}
                                      dense
                                      noHeader={false}
                                    />
                                    {detailedResults.duplicatesTotal > 20 && (
                                      <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                        Showing first 20 of {detailedResults.duplicatesTotal} duplicate records
                                      </Typography>
                                    )}
                                  </Box>
                                </AccordionItemPanel>
                              </AccordionItem>
                            )}

                            {/* Updated Records */}
                            {detailedResults['updated'] > 0 && (
                              <AccordionItem>
                                <AccordionItemHeading>
                                  <AccordionItemButton>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <CheckIcon color="info" />
                                      <Typography>Updated Records ({detailedResults.updated})</Typography>
                                    </Box>
                                  </AccordionItemButton>
                                </AccordionItemHeading>
                                <AccordionItemPanel>
                                  <Box sx={{ p: 2 }}>
                                    <Typography variant="body2">
                                      {detailedResults.updated} existing records were updated during import.
                                    </Typography>
                                    {detailedResults.updatedSample.length > 0 && (
                                      <Box sx={{ mt: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          Sample Updated Record IDs:
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                          {detailedResults.updatedSample.map(id => (
                                            <Chip key={id} label={id} size="small" variant="outlined" />
                                          ))}
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                </AccordionItemPanel>
                              </AccordionItem>
                            )}
                          </Accordion>
                        </Box>
                      )}
                    </Box>
                  )}

                  {isProcessing && !importProgress && (
                    <Box sx={{ textAlign: 'center', mt: 5 }}>
                      <CircularProgress size={60} />
                      <Typography variant="body1" sx={{ mt: 2 }}>Preparing import...</Typography>
                    </Box>
                  )}
                </Box>
              </div>
            </div >
          </>
        )

      default:
        return null;
    }
  };

  return (
    <>
      <div className='single-agent-page single-contact-page import-page'>
        <div className='page-header-new style-two'>
          <h2>
            <div className='right-heading-area ' >
              <h2>
                <button className="icon-btn-back" onClick={() => setImportWizardOpen(false)}>
                  <svg width='8' height='14' viewBox='0 0 8 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M7 13L1 7L7 1' stroke='black' strokeWidth='2' stroke-linecap='round' stroke-linejoin='round' />
                  </svg>
                </button>
                Import {object['type']} to Sell Agent </h2>
            </div>
          </h2>
        </div>
        <div className='bottom-content-area contact-details-area import-wizard-popup'>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
            {steps.map((label) => <Step key={label}> <StepLabel>{label}</StepLabel> </Step>)}
          </Stepper>
          {renderStepContent()}
        </div>
        <div className='bottom-btns-area'>
          {importStatus?.['status'] != 'completed' &&
            <Button
              onClick={handleBack}
              startIcon={<BackIcon />}
              className='btn-style-border'
              disabled={activeStep === 0 || isProcessing}
            >
              Back
            </Button>
          }
          <Button className='btn-cancel-style' onClick={() => setImportWizardOpen(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleNext}
            disabled={isProcessing}
            className='btn-style-new'
            endIcon={activeStep === steps.length - 1 ? <CheckIcon /> : <NextIcon />}
          >
            {activeStep === steps.length - 2 ? 'Import' : activeStep === steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </div>
      </div>

      {/* <Dialog maxWidth="lg" fullWidth className='import-modal'>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          </Stepper>
        </DialogContent>
        <DialogActions>
        </DialogActions>
      </Dialog> */}
    </>
  )
}

export default ImportWizard